import React, { useState, useEffect, useMemo } from "react";
import { socket } from "../socket";
import Loading from "./Loading";
import { t } from "../i18n";
import { useSounds } from "../sound";
import { alertDialog } from "../dialog";

// Smash or Pass — une collection d'images qu'on tranche une par une. Pas de
// chrono, pas de score : le rythme appartient au joueur, qui peut revenir en
// arrière autant qu'il veut. Le serveur envoie le paquet en une fois et garde
// les verdicts des comptes ; rejouer le même thème rouvre ses anciens choix.
// Voir docs/mode-smashpass.md.
function SmashPass({ gameId, player }) {
  const [deck, setDeck]       = useState(null);   // { cards, answers, theme }
  const [index, setIndex]     = useState(0);
  const [answers, setAnswers] = useState({});     // id -> { verdict, fav }
  const [recap, setRecap]     = useState(false);
  const [others, setOthers]   = useState([]);
  const [ended, setEnded]     = useState(false);   // tout le monde a fini
  const { playPop, playSuccess } = useSounds();

  const myId = player?.playerId;

  useEffect(() => {
    socket.on("spDeck", (d) => {
      setDeck(d);
      setAnswers(d.answers || {});
      setOthers(d.players || []);
      setRecap(!!d.done);
      // On reprend là où le paquet s'arrête d'être tranché : les cartes déjà
      // vues lors d'une partie précédente ne sont pas à revoir en premier.
      const first = (d.cards || []).findIndex(c => !(d.answers || {})[c.id]);
      setIndex(first === -1 ? 0 : first);
    });
    socket.on("spPlayers", ({ players }) => setOthers(players || []));
    socket.on("spEnd", () => setEnded(true));
    socket.on("spError", ({ error }) => alertDialog(error));
    socket.emit("spGetState", { gameId });
    return () => ["spDeck", "spPlayers", "spEnd", "spError"].forEach(e => socket.off(e));
  }, [gameId]);

  const cards = useMemo(() => deck?.cards || [], [deck]);
  const card  = cards[index] || null;
  const mine  = card ? answers[card.id] : null;

  // Le tableau final : les coups de cœur en tête, chacun dans sa colonne
  const columns = useMemo(() => {
    const pick = (verdict) => cards
      .filter(c => answers[c.id]?.verdict === verdict)
      .sort((a, b) => (answers[b.id]?.fav ? 1 : 0) - (answers[a.id]?.fav ? 1 : 0));
    return { smash: pick("smash"), pass: pick("pass") };
  }, [cards, answers]);

  const save = (id, next) => {
    setAnswers(a => ({ ...a, [id]: next }));
    socket.emit("spAnswer", { gameId, id, verdict: next.verdict, fav: next.fav });
  };

  const decide = (verdict) => {
    if (!card) return;
    playPop();
    save(card.id, { verdict, fav: !!mine?.fav });
    next();
  };

  const toggleFav = () => {
    if (!card) return;
    const fav = !mine?.fav;
    if (fav) playSuccess();
    // Un coup de cœur avant le verdict : on le garde, le verdict viendra
    if (mine) save(card.id, { ...mine, fav });
    else setAnswers(a => ({ ...a, [card.id]: { verdict: null, fav } }));
  };

  const next = () => setIndex(i => (i + 1 < cards.length ? i + 1 : i));
  const back = () => setIndex(i => Math.max(0, i - 1));

  const answered = cards.filter(c => answers[c.id]?.verdict).length;
  const allDone  = answered === cards.length && cards.length > 0;

  const finish = () => {
    setRecap(true);
    socket.emit("spDone", { gameId });
  };

  if (!deck) return <div class="SmashPass"><Loading label={t("smash.loading")} /></div>;

  // ── Tableau récapitulatif ──────────────────────────────────────────────────
  if (recap) {
    const waiting = others.filter(p => p.playerId !== myId && !p.done);
    return (
      <div class="SmashPass">
        <div class="sp-recap-head">
          <h2 class="sp-title">{deck.theme}</h2>
          <span class="sp-recap-count">{t("smash.recapCount", { smash: columns.smash.length, pass: columns.pass.length })}</span>
        </div>
        <div class="sp-recap">
          {["smash", "pass"].map(col => (
            <div key={col} class={`sp-col sp-col-${col}`}>
              <h3 class="sp-col-title">{t(`smash.${col}`)}</h3>
              <div class="sp-col-list">
                {columns[col].map(c => (
                  <figure key={c.id} class={`sp-mini${answers[c.id]?.fav ? " fav" : ""}`}>
                    <img src={c.image} alt={c.title} />
                    {answers[c.id]?.fav && <span class="sp-mini-fav">★</span>}
                    <figcaption>{c.title}</figcaption>
                  </figure>
                ))}
                {!columns[col].length && <p class="sp-col-empty">{t("smash.nothing")}</p>}
              </div>
            </div>
          ))}
        </div>
        <div class="sp-recap-actions">
          <button class="btn" onClick={() => setRecap(false)}>{t("smash.reviewAgain")}</button>
          {ended && others.find(p => p.playerId === myId)?.host && (
            <button class="btn btn-primary" onClick={() => socket.emit("backToLobby", { gameId })}>
              {t("smash.backToLobby")}
            </button>
          )}
          {!!waiting.length && <span class="sp-waiting">{t("smash.waiting", { n: waiting.length })}</span>}
        </div>
      </div>
    );
  }

  // ── La carte en cours ──────────────────────────────────────────────────────
  return (
    <div class="SmashPass">
      <div class="sp-head">
        <span class="sp-theme">{deck.theme}</span>
        <span class="sp-progress">{index + 1} / {cards.length}</span>
      </div>

      {card && (
        <div class={`sp-card${mine?.verdict ? ` done ${mine.verdict}` : ""}`}>
          <img class="sp-img" src={card.image} alt={card.title} />
          <button type="button" class={`sp-fav${mine?.fav ? " on" : ""}`}
            title={t("smash.favorite")} aria-label={t("smash.favorite")} onClick={toggleFav}>★</button>
          <div class="sp-legend">
            <h2 class="sp-card-title">{card.title}</h2>
            {!!card.subtitle && <p class="sp-card-sub">{card.subtitle}</p>}
          </div>
        </div>
      )}

      <div class="sp-verdicts">
        <button class={`sp-verdict pass${mine?.verdict === "pass" ? " chosen" : ""}`} onClick={() => decide("pass")}>
          {t("smash.pass")}
        </button>
        <button class={`sp-verdict smash${mine?.verdict === "smash" ? " chosen" : ""}`} onClick={() => decide("smash")}>
          {t("smash.smash")}
        </button>
      </div>

      <div class="sp-nav">
        <button class="btn sp-nav-btn" disabled={index === 0} onClick={back}>{t("smash.previous")}</button>
        {index + 1 < cards.length ? (
          <button class="btn sp-nav-btn" disabled={!mine?.verdict} onClick={next}>{t("smash.next")}</button>
        ) : (
          <button class="btn btn-primary sp-nav-btn" disabled={!allDone} onClick={finish}>{t("smash.finish")}</button>
        )}
      </div>
      <p class="sp-count">{t("smash.answered", { n: answered, total: cards.length })}</p>
    </div>
  );
}

export default SmashPass;
